import { useEffect, useState } from "react";
import { Link, useNavigate, useSearchParams } from "react-router-dom";
import api from "../lib/api";
import { eur, STATUS_LABEL } from "../lib/format";
import JourneyWizard from "../components/JourneyWizard";
import { MapPin, Loader2, Sparkles, Pencil, Lock } from "lucide-react";

export default function Trips() {
  const [bookings, setBookings] = useState(null);
  const [wizard, setWizard] = useState(null); // {mode, booking}
  const navigate = useNavigate();
  const [params, setParams] = useSearchParams();

  const load = () => api.get("/bookings").then((r) => setBookings(r.data)).catch(() => setBookings([]));
  useEffect(() => { load(); }, []);

  // Open edit wizard if arriving with ?edit=bookingId
  useEffect(() => {
    const editId = params.get("edit");
    if (editId && bookings) {
      const b = bookings.find((x) => x.booking_id === editId);
      if (b && b.amount_paid === 0) setWizard({ mode: "edit", booking: b });
      setParams({}, { replace: true });
    }
  }, [params, bookings]);

  const onSaved = (id) => { setWizard(null); load(); navigate(`/trips/${id}`); };

  if (bookings === null) return <div className="min-h-[50vh] flex items-center justify-center"><Loader2 className="w-7 h-7 text-rr-gold animate-spin" strokeWidth={1} /></div>;

  return (
    <div className="max-w-5xl mx-auto px-6 md:px-10 py-16" data-testid="trips-page">
      <div className="hairline mb-6" />
      <span className="overline">Your Escapes</span>
      <h1 className="font-heading text-6xl md:text-7xl mt-3 mb-12 text-rr-ivory">My Journeys</h1>

      {bookings.length === 0 ? (
        <div className="rounded-2xl border border-rr-gold/20 bg-rr-surface p-10 md:p-14 text-center" data-testid="design-journey-empty">
          <Sparkles className="w-8 h-8 text-rr-gold mx-auto" strokeWidth={1.2} />
          <p className="font-heading text-2xl md:text-3xl text-rr-ivory mt-6 max-w-xl mx-auto leading-snug">
            Every journey begins with intention — detox the body through Ayurveda, quiet the mind through meditation.
          </p>
          <p className="text-rr-muted mt-4 max-w-md mx-auto">Answer a few gentle questions and we'll compose your private itinerary, with your price, in moments.</p>
          <button onClick={() => setWizard({ mode: "create" })} data-testid="design-journey-btn" className="btn-gold inline-flex px-9 py-4 items-center gap-2 mt-8 text-xs uppercase">
            Design My Journey <Sparkles className="w-4 h-4" />
          </button>
        </div>
      ) : (
        <div className="space-y-6">
          {bookings.map((b) => (
            <div key={b.booking_id} data-testid={`trip-${b.booking_id}`} className="flex flex-col sm:flex-row gap-6 rounded-xl overflow-hidden border border-rr-line bg-rr-surface card-lift">
              <Link to={`/trips/${b.booking_id}`} className="sm:w-72 h-48 sm:h-auto overflow-hidden shrink-0"><img src={b.hero_image} alt="" className="w-full h-full object-cover" /></Link>
              <div className="flex-1 p-7 flex flex-col justify-center">
                <div className="flex items-center gap-1.5 mb-2"><MapPin className="w-4 h-4 text-rr-gold" strokeWidth={1.4} /><span className="text-sm text-rr-muted">{b.area}</span></div>
                <Link to={`/trips/${b.booking_id}`}><h3 className="font-heading text-3xl text-rr-ivory">{b.package_name}</h3></Link>
                <div className="flex items-center gap-4 mt-3">
                  <span className="text-xs px-3 py-1 rounded-full bg-rr-gold/15 text-rr-gold">{STATUS_LABEL[b.status] || b.status}</span>
                  <span className="text-sm text-rr-muted">{b.nights} nights · {eur(b.pricing.total)}</span>
                </div>
                <div className="mt-4">
                  {b.amount_paid === 0 ? (
                    <button onClick={() => setWizard({ mode: "edit", booking: b })} data-testid={`edit-trip-${b.booking_id}`}
                      className="btn-outline inline-flex items-center gap-2 px-5 py-2.5 text-[11px] uppercase"><Pencil className="w-3.5 h-3.5" /> Edit Journey</button>
                  ) : (
                    <span className="inline-flex items-center gap-2 text-xs text-rr-muted"><Lock className="w-3.5 h-3.5" /> Paid — contact concierge to amend</span>
                  )}
                </div>
              </div>
            </div>
          ))}
        </div>
      )}

      {wizard && <JourneyWizard mode={wizard.mode} booking={wizard.booking} onClose={() => setWizard(null)} onSaved={onSaved} />}
    </div>
  );
}
